import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { MappingEditor } from '@/features/mappings/MappingEditor';
import {
  type MappingSchemaBody,
  createMapping,
  listMappings,
} from '@/features/mappings/mappings-api';
import { getTemplate } from '@/features/templates/templates-api';
import { ApiError } from '@/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowUpRight, Loader2, Plus, Share2 } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const Route = createFileRoute('/app/templates/$id/mappings')({
  component: MappingsListPage,
});

function MappingsListPage(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const [creating, setCreating] = useState(false);

  const template = useQuery({
    queryKey: ['templates', id],
    queryFn: () => getTemplate(id),
  });
  const mappings = useQuery({
    queryKey: ['mappings', id],
    queryFn: () => listMappings(id),
  });

  const list = mappings.data?.mappings ?? [];

  return (
    <div className="space-y-6">
      <Link
        to="/app/templates/$id"
        params={{ id }}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        {t('templates.mappings.backLink')}
      </Link>

      <PageHeader
        eyebrow={t('app.layout.nav.templates', { defaultValue: 'Template' })}
        title={t('templates.mappings.title')}
        description={t('templates.mappings.subtitle', {
          name: template.data?.template.name ?? t('templates.mappings.fallbackName'),
        })}
        actions={
          !creating ? (
            <Button
              onClick={() => setCreating(true)}
              className="bg-foreground text-background hover:opacity-95"
            >
              <Plus className="size-4" aria-hidden />
              {t('templates.mappings.newButton')}
            </Button>
          ) : null
        }
      />

      {creating && template.data ? (
        <NewMappingCard
          templateId={id}
          placeholders={template.data.template.placeholders}
          onDone={() => setCreating(false)}
        />
      ) : null}

      <section className="space-y-3">
        {mappings.isLoading ? (
          <div className="grid gap-3 md:grid-cols-2">
            <Skeleton className="h-[88px] rounded-xl" />
            <Skeleton className="h-[88px] rounded-xl" />
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            icon={Share2}
            title={t('templates.mappings.empty')}
            description={t('templates.mappings.emptyBody', {
              defaultValue: 'Map the placeholders in your template to dataset fields.',
            })}
            action={
              !creating ? (
                <Button onClick={() => setCreating(true)}>
                  <Plus className="size-4" aria-hidden />
                  {t('templates.mappings.newButton')}
                </Button>
              ) : null
            }
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {list.map((m) => (
              <Link
                key={m.id}
                to="/app/mappings/$mappingId"
                params={{ mappingId: m.id }}
                className="group relative flex items-start gap-3 overflow-hidden rounded-xl border border-border/70 bg-card p-4 shadow-soft transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-elevate"
              >
                <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Share2 className="size-5" aria-hidden />
                </span>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate text-sm font-semibold tracking-tight">{m.name}</h3>
                    <Badge variant="outline" className="px-1.5 text-[10px] uppercase">
                      v{m.version}
                    </Badge>
                  </div>
                  <p className="truncate text-xs text-muted-foreground">
                    {t('templates.mappings.fieldsCount', { count: m.schema.fields.length })} ·{' '}
                    {t('templates.mappings.bindingsCount', {
                      count: Object.keys(m.schema.bindings).length,
                    })}
                  </p>
                </div>
                <ArrowUpRight
                  className="size-4 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary"
                  aria-hidden
                />
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function NewMappingCard({
  templateId,
  placeholders,
  onDone,
}: {
  templateId: string;
  placeholders: string[];
  onDone: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState(t('templates.newMapping.defaultName'));
  const [schema, setSchema] = useState<MappingSchemaBody>({
    fields: placeholders.map((ph) => ({ name: ph, type: 'string' as const, required: true })),
    bindings: Object.fromEntries(placeholders.map((ph) => [ph, ph])),
  });
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => createMapping(templateId, { name, schema }),
    onSuccess: (data) => {
      toast.success(t('templates.newMapping.success'));
      queryClient.invalidateQueries({ queryKey: ['mappings', templateId] });
      void navigate({ to: '/app/mappings/$mappingId', params: { mappingId: data.mapping.id } });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : t('templates.newMapping.failed'));
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('templates.newMapping.title')}</CardTitle>
        <CardDescription>{t('templates.newMapping.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <div className="space-y-1.5">
          <Label htmlFor="map-name">{t('templates.newMapping.nameLabel')}</Label>
          <Input id="map-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <MappingEditor placeholders={placeholders} value={schema} onChange={setSchema} />
      </CardContent>
      <CardFooter className="gap-2">
        <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || !name}>
          {mutation.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          {t('templates.newMapping.save')}
        </Button>
        <Button variant="ghost" onClick={onDone} disabled={mutation.isPending}>
          {t('templates.newMapping.cancel')}
        </Button>
      </CardFooter>
    </Card>
  );
}
